/**
 * @typedef QueryField
 * @property {string} name
 * @property {object} mapping
 * @property {string} mapping.displayName
 * @property {string} mapping.type
 * @property {string} dataSourceFieldUuid
 */

/**
 * @typedef Query
 * @property {QueryDataSource[]} dataSources
 */

/**
 * @typedef QueryDataSource
 * @property {string} alias
 * @property {boolean} isPrimary
 * @property {string} uuid Data Source UUID
 * @property {QueryField[]} fields
 */

export function isParameterized(query) {
  return query?.type === "parameterized";
}

/**
 * Whether the query should be offered in the Data Explorer. A missing flag
 * means shown — only an explicit false hides the query.
 */
export function isShownInDataExplorer(query) {
  return query?.showInDataExplorer !== false;
}

/**
 * Checks if the given query UUID is known to be parameterized based on the
 * Redux state.
 *
 * @param queryUuid
 * @param state
 * @returns {null|boolean}
 */
export function isQueryUuidParameterizedInStore(queryUuid, state) {
  for (const query of state.dataSettings?.tinyQueries ?? []) {
    if (query.uuid === queryUuid) {
      return isParameterized(query);
    }
  }
  const headerData = state.layout?.header || null;
  const blocks =
    headerData?.blocks.length > 0 ? headerData.responseData.blocks : [];
  for (const block of blocks) {
    for (const visualization of block.visualizations) {
      if (!visualization.query) {
        continue;
      }
      if (visualization.query.uuid === queryUuid) {
        return isParameterized(visualization.query);
      }
    }
  }
  for (const page of state.layout?.tabs ?? []) {
    if (!page.blocks) {
      return null;
    }

    for (const block of page.blocks) {
      for (const visualization of block.visualizations) {
        if (!visualization.query) {
          continue;
        }
        if (visualization.query.uuid === queryUuid) {
          return isParameterized(visualization.query);
        }
      }
    }
  }
  return null;
}

/**
 * @param {Query} query
 * @param {FieldNameWithAlias} fieldNameWithAlias
 * @return ?QueryFieldWithAlias
 */
export function findQueryFieldWithAliasInQuery(query, fieldNameWithAlias) {
  for (const queryDataSource of query.dataSources) {
    for (const queryField of queryDataSource.fields) {
      if (queryField.name !== fieldNameWithAlias.fieldName) {
        continue;
      }

      if (
        !fieldNameWithAlias.alias ||
        fieldNameWithAlias.alias === queryDataSource.alias
      ) {
        return new QueryFieldWithAlias(queryField, queryDataSource.alias);
      }
    }
  }
}

export function getJoinConditionFieldsByQuery(query) {
  if (!query || !query.dataSourceJoinConditions) {
    return null;
  }

  return query.dataSourceJoinConditions.flat(2).map((field) => field.fieldName);
}

export class QueryFieldWithAlias {
  /** @type {QueryField} */
  field;
  /** @type {?string} */
  alias;

  /**
   * @param field {QueryField}
   * @param alias {string=}
   */
  constructor(field, alias) {
    this.field = field;
    this.alias = alias;
  }
}

export class FieldNameWithAlias {
  /** @type {string} */
  fieldName;
  /** @type {?string} */
  alias;

  /**
   * @param fieldName {string}
   * @param alias {string=}
   */
  constructor(fieldName, alias) {
    this.fieldName = fieldName;
    this.alias = alias;
  }

  /**
   * This is mainly for tests; do not rely on it.
   **/
  toString() {
    return this.fieldName;
  }
}
